import PropTypes from "prop-types";
import React, { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import DraggableItemsBox from "../../../../components/DraggableItemsBox/DraggableItemsBox";
import HeaderButtons from "../../../../components/CourseBuilder/HeaderButtons";
import {
  courseBuilderButtonIds,
  defaultShortCourseTitle
} from "../../../../constants/index";
import CourseBuilderCourseSections from "./CourseBuilderCourseSections";
import CourseActivityIcon from "../../../../components/Icons/CourseActivityIcon";
import { useDispatch } from "react-redux";
import { addCourseSection } from "../../../../redux/actions/contentCoursesActions";
import useLoadingState from "../../../../hooks/useLoadingState";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { useTheme } from "@mui/material/styles";
import usePermissions from "../../../../redux/selectors/usePermissions";

const addNewButtons = [
  { value: courseBuilderButtonIds.upload, draggable: true },
  { value: courseBuilderButtonIds.page, draggable: true },
  { value: courseBuilderButtonIds.survey, draggable: true },
  { value: courseBuilderButtonIds.link, draggable: true },
  { value: courseBuilderButtonIds.quiz, draggable: true }
];

const addExistingButtons = [
  { value: courseBuilderButtonIds.repositoryItem, draggable: true },
  { value: courseBuilderButtonIds.course, draggable: false }
];

const getAddNewButtons = (t, allowHomeworkAssignmentSubmissions) => {
  let buttons = [...addNewButtons];
  if (allowHomeworkAssignmentSubmissions) {
    buttons.splice(4, 0, {
      value: courseBuilderButtonIds.homework,
      draggable: true
    });
  }
  return buttons.map(item => ({
    id: courseBuilderButtonIds[item.value],
    text: t(`courseBuilder.draggableItem.${item.value}`),
    icon: <CourseActivityIcon type={item.value} />,
    draggable: item.draggable
  }));
};

const getAddExistingButtons = (t, isPlatinumCustomer) => {
  const buttons = isPlatinumCustomer
    ? addExistingButtons
    : addExistingButtons.filter(
        button => button.value !== courseBuilderButtonIds.course
      );
  return buttons.map(item => ({
    id: courseBuilderButtonIds[item.value],
    text: t(`courseBuilder.draggableItem.${item.value}`),
    icon: <CourseActivityIcon type={item.value} />,
    draggable: item.draggable
  }));
};

const CourseBuilderContent = ({
  onDropItem,
  versionId,
  sectionType,
  onEditActivity,
  isPlatinumCustomer
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const isFetchingCourseSections = useLoadingState("FETCH_COURSE_SECTIONS");
  const [isDraggingIcon, setIsDraggingIcon] = useState(false);
  const { allowHomeworkAssignmentSubmissions } = usePermissions();

  const handleIsDraggingIconChange = isDraggingIcon => {
    setIsDraggingIcon(isDraggingIcon);
  };

  const handleAddShortCourse = () => {
    const data = {
      title: t(defaultShortCourseTitle),
      sectionType
    };
    dispatch(addCourseSection(versionId, data));
  };
  return (
    <>
      <HeaderButtons onAddShortCourse={handleAddShortCourse} />
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems="flex-start"
      >
        <Box
          display="flex"
          flexDirection="column"
          mr={3}
          position="sticky"
          top={theme.sizes.topNavBarHeight + 20}
        >
          <Box pb={2}>
            <DraggableItemsBox
              onIsDraggingChange={handleIsDraggingIconChange}
              buttons={getAddNewButtons(t, allowHomeworkAssignmentSubmissions)}
              header={t("courseBuilder.addNewButtonGroupHeader")}
            />
          </Box>
          <DraggableItemsBox
            onIsDraggingChange={handleIsDraggingIconChange}
            buttons={getAddExistingButtons(t, isPlatinumCustomer)}
            onButtonClick={item => onDropItem(null, item)}
            header={t("courseBuilder.addExistingButtonGroupHeader")}
          />
        </Box>
        <Box flex={1} mt={{ xs: 3, sm: 0 }}>
          {isFetchingCourseSections ? (
            <LoadingSpinner padding="100px" />
          ) : (
            <CourseBuilderCourseSections
              isDraggingIcon={isDraggingIcon}
              onDropItem={onDropItem}
              versionId={versionId}
              sectionType={sectionType}
              onEditActivity={onEditActivity}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default CourseBuilderContent;

CourseBuilderContent.propTypes = {
  onAddShortCourse: PropTypes.func,
  shortCourses: PropTypes.array,
  isAddingCourse: PropTypes.bool,
  onDropItem: PropTypes.func,
  setOpenModalType: PropTypes.func,
  setItemBeingEdited: PropTypes.func,
  onEditActivity: PropTypes.func
};
